'use strict';

import Core from './../../Core/Core';
import PBSPartnerPlayer from '@twincitiespublictelevision/pbs-partner';
import ViralEmbed from './../../ViralEmbed/ViralEmbed';
import $ from 'jquery';
import ga from './../../../vendor/google-analytics';
import VideoModel from './VideoModel.js';
import PBSPartnerPlayerEndScreen from './COVEVideoPlayerEndScreen';

import covePlayerTemplate from './views/playerCove.hbs';
import failedToLoadTemplate from './views/failedToLoadVideo.hbs';

// Namespace for private variables and functions
var privateNS = {
  defaults: { // Namespace for default options
    MINDELAY: 250,
    MAXLOADTIME: 20000,
    CHECKINTERVAL: 128
  }
};

class COVEVideoPlayer extends PBSPartnerPlayer {
  constructor(options = {}) {
    super(options);

    // Capture the container element and store it
    this._$container = $(options.container);

    // Build the fully options object
    this._options = $.extend({}, this._options, privateNS.defaults, options);

    // Set default vaules for the internal properties
    this._maxLoadTimer = null;
    this._model = null;
    this._viralEmbed = null;

    // Do an initial boot
    this.boot();
  }

  /**
   * Loads a new video model into the player. Destroys any existing data before
   * loading in the new data
   *
   * @param {VideoModel} video The video model to load
   * @returns {COVEVideoPlayer} Returns a self reference
   */
  load(video) {

    // Destroy any existing data
    this.destroy();

    // Store the model internally
    this._model = video;

    this.render();

    this.boot();

    return this;
  };

  /**
   * Unbinds from the existing UI elements. Destroys the internal COVE Message
   * API. Clears out existing loading timers. Nullifies the internal state
   * elements. Renders the empty UI to the container
   *
   * @returns {COVEVideoPlayer} Returns a self reference
   */
  destroy() {

    // Clear out any existing timeouts
    clearTimeout(this._maxLoadTimer);
    this._maxLoadTimer = null;
    clearInterval(this._loadStatusTimer);
    this._loadStatusTimer = null;

    // Clear out the model data
    this._model = null;

    // Destroy the endscreen
    if (this._endScreen) {
      this._endScreen.destroy();
      this._endScreen = null;
    }

    // Destroy the underlying objects
    PBSPartnerPlayer.prototype.destroy.call(this);

    // Clear out the UI
    this._updateUI('');

    return this;
  };

  /**
   * Renders the currently loaded video player to the container
   *
   * @returns {COVEVideoPlayer} Returns a self reference
   */
  render() {

    this._updateUI(this._buildUI());

    return this;
  };

  /**
   * Binds the necessary events to the Cove Player for loading videos
   */
  boot() {

    // Make sure that there is a video player available before binding. The
    // player may not be available if the video is not watchable
    if (this._$container.find('.cove-video-player').length > 0) {

      // Set the internal player to the player element
      this.setPlayer(this._$container.find('.cove-video-player')[0]);

      // If there is no model set (ie. initial page load) try to extract a model
      // from the video player DOM element
      if (!this._model) {

        // Wrap the attempt as parse errors will throw
        try {

          // Look for a model attribute
          var modelData = JSON.parse(this._player.getAttribute('data-model'));

          // Generate a new model from the data
          this._model = new VideoModel({ content: modelData });
        } catch (e) {
          // Fail silently
        }
      }

      // Compute the tracking url from the current page path and the video model
      var showName = window.location.pathname.match(/\/.*?\//);

      // Compute the tracking label
      var trackingLabel = [
        'show' + showName + 'video/' + this._model.getContent().slug + '/',
        this._model.getContent().tp_media_id
      ].join(' | ');

      // Set the Google Analytics function that should be tracked to
      this.analytics.setTrackingFunction(ga);

      // Register two tracking category/label pairs with the analytics plugin

      // First register tracking for the player events based on the user
      // sign in status
      this.analytics.addMediaTracking(
        Core.user.isSignedIn() ? 'Partner Player | Authenticated' : 'Partner Player | Anonymous',
        trackingLabel,
        'metric1'
      );

      // Second register tracking for the player events based on the Passport
      // status of the video
      this.analytics.addMediaTracking(
        (this._model.getContent().is_passport ? 'Passport' : 'Non-Passport') + ' Video',
        trackingLabel,
        'metric1'
      );

      // Set the reference to the cover image
      this._$videoPlayerPlaceholder = this._$container.find('.video-player-placeholder');

      // If a show id was defined, then try to load an endscreen
      if (this._options.showId) {

        // Fetch the next video next available video for this show and generate
        // an endscreen from the response
        this._model.fetchNextVideo(this._options.showId).then(function (model) {

          // If the a model was found, create an endscreen
          if (model) {

            // Create a new endscreen for the player
            this._endScreen = new PBSPartnerPlayerEndScreen({
              container: this._$container.find('.end-screen-wrapper'),
              model: model,
              navigator: this._options.navigator
            });
          }
        }.bind(this));
      }

      // Bind the initialize step of the Cove player
      this.on(
        'initialize',
        this._onCovePlayerInitialized.bind(this)
      );

      // Create and bind a handler for when the video ends
      this._videoCompleteHandler = this._onCovePlayerComplete.bind(this);

      this.on(
        'complete',
        this._videoCompleteHandler
      );

      // Schedule a default timer that trigger the initialization handler after
      // a timeout. This is set up to handle potential errors with the Cove
      // player so that the user is not left with and uninteractable image
      this._maxLoadTimer = setTimeout(
        this._onCovePlayerFailed.bind(this),
        this._options.MAXLOADTIME
      );

      this._loadStatusTimer = setInterval(
        this._checkPlayerLoadStatus.bind(this),
        this._options.CHECKINTERVAL
      );
    }
  };

  /**
   * Generates the markup string that represents the full player UI.
   *
   * @returns {string} The markup representing the player UI
   * @private
   */
  _buildUI() {

    // Compute features about this video in respect to the logged in user
    var userCanPlayVideo = !this._model.getContent().is_passport ||
      (this._model.getContent().minimum_access_level !== false && Core.user.getAccessLevel() >= this._model.getContent().minimum_access_level);

    // Generate the necessary data bundles to display the UI elements
    var playerDataBundle = {
      autoplay: userCanPlayVideo,
      videoId: this._model.getContent().tp_media_id
    };

    return covePlayerTemplate(playerDataBundle);
  };

  /**
   * Replaces the player container with the passed in ui element(s)
   *
   * @param {string} ui The string representation of the ui
   * @private
   */
  _updateUI(ui) {

    this._$container.html(ui);
  };

  /**
   * Called when the content of the embed element is copied
   * @private
   */
  _onCopyEmbedCode() {
    ga('send', 'event', 'social', 'embed/player', window.location.href);
  };

  _checkPlayerLoadStatus() {

    // The player is defined as loaded if one of the following are true
    // 1. The frame is has a different origin, meaning a redirect occured
    // 2. The frame contents is able to be inspected and it contains a player
    if (!this._testAccessToFrame(this._player) ||
      $(this._player.contentDocument || this._player.contentWindow.document).find('.player').length > 0) {
      this._onCovePlayerInitialized();
    }
  };

  /**
   * Called whenever the show pages Cove Player initializes. Disables the failed
   * to load timeout and hides the cover image
   * @private
   */
  _onCovePlayerInitialized() {

    // Once the initialization handler triggers, remove any pending max load
    // timers that are hanging around
    clearTimeout(this._maxLoadTimer);
    clearInterval(this._loadStatusTimer);

    // When the player initializes, check to see if there is an error message.
    // If there is then log a did not load message
    if (this._testAccessToFrame(this._player) &&
      $(this._player.contentDocument || this._player.contentWindow.document).find('#tpt-cove-player-error').length > 0) {
      Core.Rollbar.log(
        'Player did not load',
        {
          signedIn: Core.user.isSignedIn(),
          userAccessLevel: Core.user.getAccessLevel(),
          userExpired: Core.user.isExpired(),
          videoAccessLevel: this._model.getContent().minimum_access_level,
          message: $(this._player.contentDocument || this._player.contentWindow.document).find('#tpt-cove-player-error').html(),
          playerMessages: {
            incoming: this.messages.incoming.map(function (e) {
              return e.data;
            }),
            outgoing: this.messages.outgoing
          }
        }
      );
    }

    // When the player finishes initializing, remove the video loading screen
    // if the user has the appropriate level of Passport access
    this._$videoPlayerPlaceholder.hide();
  };

  /**
   * Called whenever the show pages Cove Player fails to load. Destroys the
   * current player and replaces it with a failed to load message
   */
  _onCovePlayerFailed() {

    // Log a failure to load
    Core.Rollbar.log(
      'Player failed to load in time',
      {
        signedIn: Core.user.isSignedIn(),
        userAccessLevel: Core.user.getAccessLevel(),
        userExpired: Core.user.isExpired(),
        videoAccessLevel: this._model.getContent().minimum_access_level,
        frameOriginMatch: this._testAccessToFrame(this._player),
        playerMessages: {
          incoming: this.messages.incoming.map(function (e) { return e.data; }),
          outgoing: this.messages.outgoing
        }
      }
    );

    // Destroy the current player
    this.destroy();

    // Update the page with the failed to load message
    this._updateUI(failedToLoadTemplate());
  };

  /**
   * Attempts to access the iframe player. This is a potentially unsafe
   * operation as the origin of the iframe changes from TPT to PBS after loading
   *
   * NOTE: This function is subject to potential race conditions. The iframe can
   * potentially change origins in between calling this function and performing
   * some action afterwards
   *
   * @returns {boolean} Returns true if the frame can be safely access and false
   *                    otherwise
   * @private
   */
  _testAccessToFrame(frame) {

    // This operation is potentially unsafe. A security exception may be
    // thrown if the iframe points to a different domain (ie. COVE)
    try {
      if (frame.contentDocument || frame.contentWindow.document) {

        // If either the content document for the document of the contentWindow
        // can be accessed, then the iframe is on the same domain as the host
        return true;
      }
    } catch (exception) {

      // If a security exception was thrown, then it is unsafe to try and access
      // the contents of the iframe. Ignore the exception and let the failure
      // fall through
    }

    return false;
  };

  /**
   * Called whenever the show pages Cove Player completes playback. Displays the
   * end screen with the share options for the video
   */
  _onCovePlayerComplete() {

    // If an end screen has been created, render it and start the timer
    if (this._endScreen) {

      // Render the end screen
      this._endScreen.render();

      // If the user is not on a mobile device, also start the auto advance timer
      this._endScreen.startAutoplayTimer();
    }

    this.off('complete', this._videoCompleteHandler);
  };
}

export default COVEVideoPlayer